import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Budget {
  caps: { questionCap: number; tokenBudget: number };
  usage: { questions: number; tokens: number };
  questionsLeft: number;
  exhausted: boolean;
}

interface AlignmentConfig { autoAnalyze: boolean; mode: 'per_message' | 'batch'; threshold: number; autoConfirmShared: boolean }
interface InterviewConfig { maxPerDay: number; maxPerCategory: number; queueTarget: number; interestProb: number; newsProb: number }
interface AutoBuildConfig { enabled: boolean; dailyTarget: number; askPerDay: number; model: string; costCapUsd: number }

export function AnalysisPane() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ budget: Budget }>({
    queryKey: ['analysis', 'budget'],
    queryFn: () => api('/api/analysis/budget'),
  });
  const cfg = useQuery<{ alignment: AlignmentConfig; interview: InterviewConfig; autobuild: AutoBuildConfig }>({
    queryKey: ['admin', 'config'],
    queryFn: () => api('/api/admin/config'),
  });
  const a = cfg.data?.alignment;
  const iv = cfg.data?.interview;
  const ab = cfg.data?.autobuild;

  const [abMsg, setAbMsg] = useState<string | null>(null);
  const runAutoBuild = useMutation({
    mutationFn: () => api<{ ok: boolean; reason?: string; generated: number; asked: number; autoFiled: number; pendingReview: number }>('/api/interview/autobuild/run', { method: 'POST' }),
    onMutate: () => setAbMsg('Running…'),
    onSuccess: (r) => {
      setAbMsg(r.ok
        ? `Generated ${r.generated} — ${r.asked} queued for you, ${r.autoFiled} filed (${r.pendingReview} to review).`
        : r.reason === 'no_api' ? 'Needs API mode + an Anthropic key.' : (r.reason ?? 'nothing to do'));
      qc.invalidateQueries({ queryKey: ['memory'] });
      qc.invalidateQueries({ queryKey: ['interview'] });
    },
    onError: () => setAbMsg('Run failed.'),
  });

  async function patchAlign(patch: Record<string, string | number | boolean>) {
    await api('/api/admin/settings', { method: 'PATCH', body: JSON.stringify(patch) });
    qc.invalidateQueries({ queryKey: ['admin', 'config'] });
    qc.invalidateQueries({ queryKey: ['analysis'] });
  }

  const [qCap, setQCap] = useState('');
  const [tBudget, setTBudget] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const inited = (qCap !== '' || tBudget !== '');

  useEffect(() => {
    if (data?.budget && !inited) {
      setQCap(String(data.budget.caps.questionCap));
      setTBudget(String(data.budget.caps.tokenBudget));
    }
  }, [data, inited]);

  async function save() {
    setSaving(true); setSaved(false);
    try {
      await api('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          'analysis.dailyQuestionCap': Number(qCap) || 10,
          'analysis.dailyTokenBudget': Number(tBudget) || 120000,
        }),
      });
      qc.invalidateQueries({ queryKey: ['analysis'] });
      setSaved(true);
    } finally { setSaving(false); }
  }

  return (
    <>
      {/* Auto-alignment from conversations */}
      <div className="mb-6">
        <h3 className="font-serif text-2xl text-ink m-0">Auto-alignment</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          After conversations, the twin self-assesses its replies. Answers below the confidence
          threshold are forced into the alignment queue for you to confirm or correct.
        </p>

        {a == null ? <div className="text-ink/40 text-[12px] py-4">Loading…</div> : (
          <div className="mt-3">
            <div className="grid grid-cols-[200px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
              <div className="text-[11px] text-ink/45">Auto-analyze</div>
              <button onClick={() => patchAlign({ 'alignment.autoAnalyze': !a.autoAnalyze })}
                className={`px-4 py-1.5 rounded-full text-[11.5px] border ${ a.autoAnalyze ? 'border-emerald-300/40 text-emerald-300' : 'border-ink/20 text-ink/50' }`}>
                {a.autoAnalyze ? 'On' : 'Off'}
              </button>
            </div>

            <div className="grid grid-cols-[200px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
              <div className="text-[11px] text-ink/45">When</div>
              <div className="flex gap-2">
                {([['batch', 'End of conversation'], ['per_message', 'Every reply']] as const).map(([val, label]) => (
                  <button key={val} onClick={() => patchAlign({ 'alignment.mode': val })}
                    className={`px-3.5 py-1.5 rounded-full text-[11.5px] border ${ a.mode === val ? 'bg-ink text-void border-ink' : 'border-ink/15 text-ink/55 hover:text-ink' }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-[200px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
              <div className="text-[11px] text-ink/45">Auto-confirm shared</div>
              <div className="flex items-center gap-3">
                <button onClick={() => patchAlign({ 'alignment.autoConfirmShared': !a.autoConfirmShared })}
                  className={`px-4 py-1.5 rounded-full text-[11.5px] border ${ a.autoConfirmShared ? 'border-sky-300/50 text-sky-300' : 'border-ink/20 text-ink/50' }`}>
                  {a.autoConfirmShared ? 'On' : 'Off'}
                </button>
                <span className="font-mono text-[10px] text-ink/40">accept the twin’s stance automatically for public/colleague outcomes</span>
              </div>
            </div>

            <div className="grid grid-cols-[200px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
              <div className="text-[11px] text-ink/45">Force-align threshold</div>
              <div className="flex items-center gap-2">
                <input type="number" min={1} max={100} defaultValue={a.threshold} key={a.threshold}
                  onBlur={(e) => { const n = Number(e.target.value); if (n >= 1 && n <= 100 && n !== a.threshold) patchAlign({ 'alignment.threshold': n }); }}
                  className="w-24 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
                <span className="font-mono text-[10px] text-ink/40">% — below this, an answer needs your alignment</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Today / interview tuning */}
      <div className="mb-6">
        <h3 className="font-serif text-2xl text-ink m-0">Today — interview</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          How the daily interview paces itself: questions per day, per life-chapter, how deep the
          fresh-question queue stays, and how often it dips into your interests / the news.
        </p>
        {iv == null ? <div className="text-ink/40 text-[12px] py-4">Loading…</div> : (
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6">
            {([
              ['interview.maxPerDay', 'Questions per day', iv.maxPerDay, 1, 50, 1],
              ['interview.maxPerCategory', 'Max per life-chapter / day', iv.maxPerCategory, 1, 20, 1],
              ['interview.queueTarget', 'Fresh-question queue depth', iv.queueTarget, 1, 100, 1],
              ['interview.interestProb', 'Interest-probe chance', iv.interestProb, 0, 1, 0.05],
              ['interview.newsProb', 'News-seed chance', iv.newsProb, 0, 1, 0.05],
            ] as const).map(([key, label, val, min, max, step]) => (
              <div key={key} className="grid grid-cols-[200px_1fr] gap-4 items-center py-3 border-b border-ink/[0.06]">
                <div className="text-[11px] text-ink/45">{label}</div>
                <input type="number" min={min} max={max} step={step} defaultValue={val} key={`${key}-${val}`}
                  onBlur={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n >= min && n <= max && n !== val) patchAlign({ [key]: n }); }}
                  className="w-24 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Autonomous twin-builder */}
      <div className="mb-6">
        <h3 className="font-serif text-2xl text-ink m-0">Auto-build <span className="text-[11.5px] text-ink/40 align-middle">beta</span></h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          The twin reads fresh news tied to your interests and drafts its own reactions. The least-confident
          few become questions for you in Today; the rest are filed as “auto” memories — shareable ones
          auto-confirmed, sensitive ones flagged for review in the Vault. Costs API tokens per run.
        </p>
        {ab == null ? <div className="text-ink/40 text-[12px] py-4">Loading…</div> : (
          <div className="mt-3">
            <div className="grid grid-cols-[200px_1fr] gap-4 items-center py-3 border-b border-ink/[0.06]">
              <div className="text-[11px] text-ink/45">Enabled (daily)</div>
              <button onClick={() => patchAlign({ 'autobuild.enabled': !ab.enabled })}
                className={`px-4 py-1.5 rounded-full text-[11.5px] border ${ab.enabled ? 'border-emerald-300/40 text-emerald-300' : 'border-ink/20 text-ink/50'}`}>
                {ab.enabled ? 'On' : 'Off'}
              </button>
            </div>
            {([
              ['autobuild.dailyTarget', 'Items per day', ab.dailyTarget, 1, 100, 1],
              ['autobuild.askPerDay', 'Ask me per day', ab.askPerDay, 0, 20, 1],
              ['autobuild.costCapUsd', 'Daily cost cap (USD)', ab.costCapUsd, 0, 1000, 0.5],
            ] as const).map(([key, label, val, min, max, step]) => (
              <div key={key} className="grid grid-cols-[200px_1fr] gap-4 items-center py-3 border-b border-ink/[0.06]">
                <div className="text-[11px] text-ink/45">{label}</div>
                <input type="number" min={min} max={max} step={step} defaultValue={val} key={`${key}-${val}`}
                  onBlur={(e) => { const n = Number(e.target.value); if (Number.isFinite(n) && n >= min && n <= max && n !== val) patchAlign({ [key]: n }); }}
                  className="w-24 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
              </div>
            ))}
            <div className="mt-3 flex items-center gap-3">
              <button onClick={() => runAutoBuild.mutate()} disabled={runAutoBuild.isPending}
                className="px-5 py-2 rounded-full bg-particle text-void text-[11.5px] hover:opacity-90 disabled:opacity-40">
                {runAutoBuild.isPending ? 'Running…' : 'Run now'}
              </button>
              {abMsg && <span className="font-serif italic text-[12.5px] text-ink/60">{abMsg}</span>}
            </div>
          </div>
        )}
      </div>

      <div className="mb-4">
        <h3 className="font-serif text-2xl text-ink m-0">Analysis budget</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          The alignment engine forms stances on the news. Cap how many it generates per day and the API
          token budget it may spend (enforced once a live key is set; in dev, Claude Desktop drives it for free).
        </p>
      </div>

      {isLoading || !data ? <div className="text-ink/40 text-[12px] py-6">Loading…</div> : (
        <>
          <div className="grid grid-cols-3 gap-5 mb-6 py-3 border-b border-ink/[0.06]">
            <Stat n={`${data.budget.usage.questions} / ${data.budget.caps.questionCap}`} label="questions today" />
            <Stat n={data.budget.usage.tokens.toLocaleString()} label="tokens today" />
            <Stat n={data.budget.exhausted ? 'reached' : `${data.budget.questionsLeft} left`} label="status" />
          </div>

          <div className="grid grid-cols-[200px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
            <div className="text-[11px] text-ink/45">Daily question cap</div>
            <input type="number" value={qCap} onChange={(e) => { setQCap(e.target.value); setSaved(false); }}
              className="w-32 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
          </div>
          <div className="grid grid-cols-[200px_1fr] gap-5 items-center py-3 border-b border-ink/[0.06]">
            <div className="text-[11px] text-ink/45">Daily token budget</div>
            <input type="number" value={tBudget} onChange={(e) => { setTBudget(e.target.value); setSaved(false); }}
              className="w-40 px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-mono text-[14px] text-ink focus:border-ink/40 focus:outline-none" />
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={saving}
              className="px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-40">
              {saving ? 'Saving…' : 'Save caps'}
            </button>
            {saved && <span className="text-[11px] text-emerald-300/80">saved</span>}
          </div>
        </>
      )}
    </>
  );
}

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <div className="font-serif text-2xl text-ink">{n}</div>
      <div className="text-[11px] text-ink/40 mt-1">{label}</div>
    </div>
  );
}
